import { InfoItem } from 'components/molecules/InfoItem/InfoItem';
import { Formik, useField } from 'formik';
import {
  CoderbyteWorkspaceFieldsFragment,
  useCoderbyteWorkspaceApiKeyLazyQuery,
  useSetCoderbyteWorkspaceApiKeyMutation,
  useVerifyCoderbyteWorkspaceApiKeyMutation,
} from 'generated/graphql';
import { useState } from 'react';
import {
  Button,
  Checkbox,
  FormikTextInputField,
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import { formatTimestamp } from 'helpers/date';
import styles from './CoderbyteCredentials.module.scss';

const dataTestId = 'coderbyte-api-key';

type ApiKeyCardProps = {
  companyId: number;
  workspace: CoderbyteWorkspaceFieldsFragment;
  onChanged: () => void;
};

/**
 * The API key we use for every Coderbyte call made on this company's behalf.
 */
export function ApiKeyCard({
  companyId,
  workspace,
  onChanged,
}: ApiKeyCardProps) {
  const { handleMsgType } = useNotification();
  const [isEditing, setIsEditing] = useState(!workspace.keySetAt);
  const [revealed, setRevealed] = useState<string | null>(null);

  const [reveal, { loading: revealing }] = useCoderbyteWorkspaceApiKeyLazyQuery(
    {
      fetchPolicy: 'network-only',
      onCompleted: result =>
        setRevealed(result.coderbyteWorkspaceApiKey ?? null),
      onError: error => {
        handleMsgType({
          type: TNotification.error,
          title: 'Could not reveal the API key',
          message: error.message,
          dismissable: true,
        });
      },
    }
  );

  const [verify, { loading: verifying }] =
    useVerifyCoderbyteWorkspaceApiKeyMutation({
      variables: { companyId },
      onCompleted: result => {
        const verifiedAt =
          result.verifyCoderbyteWorkspaceApiKey.keyVerifiedAt ?? null;
        // verifyApiKey reports failure by leaving the timestamp alone rather
        // than erroring, so a moved timestamp is what "it worked" looks like.
        const succeeded =
          !!verifiedAt && verifiedAt !== (workspace.keyVerifiedAt ?? null);

        handleMsgType({
          type: succeeded ? TNotification.success : TNotification.error,
          title: succeeded
            ? 'API key is working.'
            : 'Coderbyte did not accept the stored key',
          message: succeeded
            ? undefined
            : 'The key may have been replaced in Coderbyte, or Coderbyte may be unreachable. Nothing has been changed.',
          dismissable: true,
        });
        onChanged();
      },
      onError: error => {
        handleMsgType({
          type: TNotification.error,
          title: 'Could not check the API key',
          message: error.message,
          dismissable: true,
        });
      },
    });

  if (isEditing) {
    return (
      <ApiKeyForm
        companyId={companyId}
        webhookUrl={workspace.webhookUrl}
        onCancel={workspace.keySetAt ? () => setIsEditing(false) : undefined}
        onSaved={() => {
          setIsEditing(false);
          setRevealed(null);
          onChanged();
        }}
      />
    );
  }

  return (
    <div className={styles.card} data-testid={dataTestId}>
      <p className={styles.cardTitle}>API key</p>
      <InfoItem
        label="Entered"
        value={formatTimestamp(workspace.keySetAt)}
        valueNote="Used for every call we make on this company's behalf."
      />
      <InfoItem
        label="Last verified"
        value={formatTimestamp(workspace.keyVerifiedAt)}
      />
      {revealed && <InfoItem label="API key" value={revealed} hasCopyButton />}

      <div className={styles.buttonRow}>
        {revealed ? (
          <Button
            variant="secondary"
            size="medium"
            onClick={() => setRevealed(null)}
          >
            Hide
          </Button>
        ) : (
          <Button
            variant="secondary"
            size="medium"
            disabled={revealing}
            onClick={() => reveal({ variables: { companyId } })}
            data-testid={`${dataTestId}__reveal`}
          >
            {revealing ? 'Revealing…' : 'Reveal key'}
          </Button>
        )}
        <Button
          variant="secondary"
          size="medium"
          onClick={() => setIsEditing(true)}
          data-testid={`${dataTestId}__replace`}
        >
          Replace key
        </Button>
        <Button
          variant="secondary"
          size="medium"
          disabled={verifying}
          onClick={() => verify()}
          data-testid={`${dataTestId}__verify`}
        >
          {verifying ? 'Checking…' : 'Check key'}
        </Button>
      </div>
    </div>
  );
}

type ApiKeyFormProps = {
  companyId: number;
  webhookUrl: string;
  onSaved: () => void;
  onCancel?: () => void;
};

/**
 * Write-only: the stored key is never pre-filled, because a form that showed
 * it would imply editing in place rather than replacing.
 *
 * The key is proved against Coderbyte before it is stored, so a bad paste
 * fails here rather than weeks later when the first candidate needs inviting.
 *
 * The webhook step rides along here rather than in a card of its own because
 * this is the one moment the person is already in Coderbyte's API settings
 * page with it open — the webhook is registered on the same screen the key is
 * copied from. Splitting them would mean asking them to go back.
 *
 * It is gated rather than merely mentioned because forgetting it is both easy
 * and invisible: Coderbyte exposes no API for webhook registration, so nothing
 * here can set it, read it back, or ever warn that it is missing. A workspace
 * without it looks completely healthy — the key verifies, candidates sit their
 * assessments — and every result is silently dropped.
 */
function ApiKeyForm({
  companyId,
  webhookUrl,
  onSaved,
  onCancel,
}: ApiKeyFormProps) {
  const { handleMsgType } = useNotification();
  const [webhookRegistered, setWebhookRegistered] = useState(false);

  const [setApiKey, { loading }] = useSetCoderbyteWorkspaceApiKeyMutation({
    onCompleted: () => {
      handleMsgType({
        type: TNotification.success,
        title: 'API key saved and verified.',
        dismissable: true,
      });
      onSaved();
    },
    onError: error => {
      handleMsgType({
        type: TNotification.error,
        title: 'Coderbyte rejected that API key',
        message: `${error.message} Nothing has been saved.`,
        dismissable: true,
      });
    },
  });

  return (
    <div className={styles.card}>
      <p className={styles.cardTitle}>API key</p>
      <p className={styles.caption}>
        Copy the key from the API settings page of this workspace&apos;s
        Coderbyte account. It is checked against Coderbyte before being saved.
      </p>

      <div className={styles.warning} data-testid={`${dataTestId}__webhook`}>
        <p className={styles.warningTitle}>
          Register the webhook before you save
        </p>
        <p className={styles.warningBody}>
          While you are on that same API settings page, select{' '}
          <strong>Edit webhooks</strong> and paste in the URL below. It is how
          Coderbyte sends us completed assessments.
        </p>
        <p className={styles.warningBody}>
          <strong>
            If you skip this, nothing will appear to be wrong and every result
            will be lost.
          </strong>{' '}
          Coderbyte gives us no way to set this for you, no way to read back
          whether it is there, and no warning if it is missing. Candidates will
          sit their assessments and their scores will never reach us.
        </p>

        <InfoItem label="Webhook URL" value={webhookUrl} hasCopyButton />

        <Checkbox
          id="coderbyte-webhook-registered"
          label="I have registered this webhook URL in Coderbyte"
          checked={webhookRegistered}
          onChange={() => setWebhookRegistered(current => !current)}
        />
      </div>

      <Formik
        initialValues={{ apiKey: '' }}
        onSubmit={values =>
          setApiKey({ variables: { companyId, apiKey: values.apiKey.trim() } })
        }
      >
        {({ handleSubmit, values }) => (
          <form onSubmit={handleSubmit} className={styles.form}>
            <FormikTextInputField
              id="apiKey"
              label="API key"
              type="password"
              autoComplete="off"
              useFormikField={useField}
            />
            <div className={styles.buttonRow}>
              <Button
                type="submit"
                variant="primary"
                size="medium"
                disabled={
                  loading || !values.apiKey.trim() || !webhookRegistered
                }
                data-testid={`${dataTestId}__save`}
              >
                {loading ? 'Checking…' : 'Save key'}
              </Button>
              {onCancel && (
                <Button
                  variant="secondary"
                  size="medium"
                  disabled={loading}
                  onClick={onCancel}
                >
                  Cancel
                </Button>
              )}
            </div>
          </form>
        )}
      </Formik>
    </div>
  );
}
